/**
 * Where the hole goes, where the card stands, and how far the page has to move
 * to leave room for both.
 *
 * Kept apart from the component because it is the only part of a spotlight that
 * can be reasoned about without a browser: everything else is measuring,
 * scrolling and transitions.
 */

export interface SpotRect {
  top: number
  left: number
  width: number
  height: number
}

/** Breathing room around the highlighted element, so it is lit and not traced. */
export const SPOT_PAD = 8

/** Between the hole and the card, so the ring is never crowded by the text. */
export const CARD_GAP = 14

/** What the card is assumed to need before it has been measured once. */
export const CARD_MIN_PX = 300

/** The tab bar the card may not stand on. */
export const TAB_BAR_PX = 68

/** Clear of the status bar on the screens that have one. */
export const TOP_INSET_PX = 8

export type CardSide = 'top' | 'bottom' | 'center'

/**
 * Pad a measured element and keep the result inside the viewport.
 *
 * A padded rect that runs off the top would put the cut-out's rounded corner
 * where no corner is, which reads as a rendering fault rather than as a
 * highlight of something partly scrolled away.
 */
export function padSpot(
  rect: { top: number; left: number; width: number; height: number },
  viewport: { width: number; height: number },
  pad: number = SPOT_PAD,
): SpotRect {
  const top = Math.max(0, rect.top - pad)
  const left = Math.max(0, rect.left - pad)
  const bottom = Math.min(viewport.height, rect.top + rect.height + pad)
  const right = Math.min(viewport.width, rect.left + rect.width + pad)
  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  }
}

export interface Band {
  top: number
  bottom: number
}

/**
 * The strip of screen left for the highlight once the card has taken its side.
 *
 * This is the whole trick. On a phone the card is nearly half the screen, so
 * centring the highlighted element and hoping leaves the card sitting on it —
 * which is what the first cut of this did. Reserving the card's side first and
 * scrolling the element into what remains is what makes an overlap impossible
 * rather than unlikely.
 */
export function spotBand(side: 'top' | 'bottom', viewportHeight: number, cardHeight: number): Band {
  return side === 'bottom'
    ? {
        top: TOP_INSET_PX,
        bottom: viewportHeight - TAB_BAR_PX - cardHeight - CARD_GAP * 2,
      }
    : {
        top: cardHeight + CARD_GAP * 2,
        bottom: viewportHeight - TAB_BAR_PX,
      }
}

/** How far the page must move for `rect` to sit in the middle of `band`. */
export function scrollDelta(rect: { top: number; height: number }, band: Band): number {
  const bandCentre = (band.top + band.bottom) / 2
  return rect.top + rect.height / 2 - bandCentre
}

/** Whether the element ended up inside the strip reserved for it. */
export function fitsBand(rect: { top: number; height: number }, band: Band, slack = 1): boolean {
  return rect.top >= band.top - slack && rect.top + rect.height <= band.bottom + slack
}

/**
 * How far the page must move to shove `rect` against one end of `band`.
 *
 * For the highlights that cannot fit at all — a settings card taller than the
 * strip on a small phone. Centring such a thing loses both its ends under the
 * card; pushing it to the far end loses only the tail, and a card whose first
 * rows are visible is a card somebody can still recognise.
 */
export function pushDelta(
  rect: { top: number; height: number },
  band: Band,
  toward: 'start' | 'end',
): number {
  return toward === 'start' ? rect.top - band.top : rect.top + rect.height - band.bottom
}

/**
 * The side to fall back to when the highlight fits neither band — a control
 * taller than half the screen, or one pinned where it cannot be scrolled away
 * from. The card takes the roomier side and the clamp in CSS does the rest.
 */
export function roomierSide(
  rect: { top: number; height: number },
  viewportHeight: number,
): 'top' | 'bottom' {
  const below = viewportHeight - (rect.top + rect.height) - TAB_BAR_PX
  return below >= rect.top ? 'bottom' : 'top'
}

/** Two measurements of the same element, close enough to call it settled. */
export function rectsAgree(a: SpotRect | null, b: SpotRect | null, epsilon = 0.5): boolean {
  if (a == null || b == null) return false
  return (
    Math.abs(a.top - b.top) < epsilon &&
    Math.abs(a.left - b.left) < epsilon &&
    Math.abs(a.width - b.width) < epsilon &&
    Math.abs(a.height - b.height) < epsilon
  )
}

/** The nearest ancestor that actually scrolls, or null when nothing does. */
export function scrollerOf(element: Element): HTMLElement | null {
  let node = element.parentElement
  while (node != null) {
    const overflow = getComputedStyle(node).overflowY
    if (
      (overflow === 'auto' || overflow === 'scroll') &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}
